import { useCallback, useEffect, useMemo } from "react";

import { facilityValidationSchema, FormValues } from "components/Deal-amortization/schemas";
import { FormikErrors, FormikTouched, useFormik } from "formik";
import { AltiusErrors, AltiusRes, DebtSegment, FacilityFee } from "store/types";
import { defaultFacilityValues } from "utils/debt";

import type { DealTab } from "./use-facility-tabs";

// Calculate auto-filled values based on payment term and other fields
function calculateAutoFilledValues<T extends Partial<FormValues>>(values: T) {
  const shouldAutoFillDates = values.is_withdrawal;

  if (values.payment_term === "balloon") {
    return {
      balloon_payment: values.loan_amount || 0,
      principal_first_payment_date: shouldAutoFillDates ? values.maturity_date : null,
      interest_first_payment_date: shouldAutoFillDates ? values.maturity_date : null
    };
  } else if (values.payment_term === "partial_balloon") {
    return {
      principal_first_payment_date: shouldAutoFillDates ? values.maturity_date : null,
      balloon_payment: values.loan_amount || 0
    };
  } else if (values.payment_term === "custom") {
    return {
      principal_first_payment_date: shouldAutoFillDates ? values.maturity_date : null
    };
  } else {
    return {
      balloon_payment: 0
    };
  }
}

interface UseFacilityFormProps {
  currentSegment?: DebtSegment;
  currentPrimaryTab?: DealTab;
  isFacilityTab: boolean;
  fees?: FacilityFee[];
  facilitySegments: DebtSegment[];
  primaryTabIndex: number;
  secondaryTabIndex: number;
  onApply: (
    segments: DebtSegment[],
    dealId?: number,
    fees?: FacilityFee[]
  ) => Promise<AltiusRes<DebtSegment> | AltiusErrors | void>;
  updateSegmentOnState: (segmentData: DebtSegment) => DealTab;
  clearTabDirty: () => void;
  markTabDirty: () => void;
  setIsValuesDrawerOpen: (open: boolean) => void;
  onClose: () => void;
}

export function useFacilityForm({
  currentSegment,
  currentPrimaryTab,
  isFacilityTab,
  fees,
  facilitySegments,
  primaryTabIndex,
  secondaryTabIndex,
  onApply,
  updateSegmentOnState,
  clearTabDirty,
  markTabDirty,
  setIsValuesDrawerOpen,
  onClose
}: UseFacilityFormProps) {
  const initialValues = useMemo(() => {
    if (currentSegment) {
      return {
        ...currentSegment,
        deal_title: currentSegment?.deal_title || "",
        overriding_parent: false,
        fees: isFacilityTab ? fees || [] : [],
        interest_index_value: null,
        principal_index_value: null,
        pik_index_value: null,
        custom_payments_file: null
      } as FormValues;
    }
    return {
      ...defaultFacilityValues,
      is_facility: isFacilityTab,
      is_withdrawal: !isFacilityTab,
      fees: isFacilityTab ? fees || [] : []
    };
  }, [currentSegment, fees, isFacilityTab]);

  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema: facilityValidationSchema,
    onSubmit: async () => {
      if (currentPrimaryTab?.isNew) {
        setIsValuesDrawerOpen(true);
      } else {
        await handleSubmit();
        onClose();
      }
    }
  });

  const getSegmentFromFormValues = useCallback(() => {
    const {
      deal_title,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      interest_index_value,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      principal_index_value,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      pik_index_value,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      custom_payments_file,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      fees,
      ...rest
    } = formik.values;

    const segmentData: DebtSegment = {
      ...rest,
      id: currentSegment?.id || 0,
      deal_id: currentPrimaryTab?.dealId || 0,
      deal_title: deal_title
    };

    return segmentData;
  }, [formik, currentSegment, currentPrimaryTab, isFacilityTab]);

  const handleSubmit = async () => {
    try {
      // Save current state first
      const segment = getSegmentFromFormValues();
      const currentTab = updateSegmentOnState(segment);

      // Send all segments for the current deal, and fees if it's a facility
      const feesToSend = currentTab.isFacility ? formik.values.fees : undefined;
      await onApply(currentTab.segments, currentTab.dealId, feesToSend);

      // Clear dirty state for this tab
      clearTabDirty();
    } catch (error: unknown) {
      console.log("Error:", error);
      const reponse = (error as { response?: AltiusErrors })?.response;
      // Map server errors to Formik format
      if (reponse?.errors) {
        const serverErrors = reponse.errors;
        const formikErrors: FormikErrors<FormValues> = {};
        const formikTouched: FormikTouched<FormValues> = {};

        Object.keys(serverErrors).forEach((key) => {
          const errorMessages = serverErrors[key];
          const message = Array.isArray(errorMessages) ? errorMessages[0] : errorMessages;

          // Handle nested errors like "fees.1.start_date"
          const parts = key.split(".");
          if (parts.length === 3 && parts[0] === "fees") {
            const index = parseInt(parts[1], 10);
            const field = parts[2];
            if (!formikErrors.fees || !Array.isArray(formikErrors.fees)) {
              formikErrors.fees = [];
            }
            const feesErrors = formikErrors.fees as Record<string, string>[];
            if (!feesErrors[index]) {
              feesErrors[index] = {};
            }
            feesErrors[index][field] = message;

            // Mark field as touched
            if (!formikTouched.fees || !Array.isArray(formikTouched.fees)) {
              formikTouched.fees = [];
            }
            const feesTouched = formikTouched.fees as Record<string, boolean>[];
            if (!feesTouched[index]) {
              feesTouched[index] = {};
            }
            feesTouched[index][field] = true;
          } else if (key !== "fees") {
            // Handle top-level errors (skip "fees" as it would be an array type)
            formikErrors[key as Exclude<keyof FormValues, "fees">] = message;
            formikTouched[key as Exclude<keyof FormValues, "fees">] = true;
          }
        });

        formik.setErrors(formikErrors);
        formik.setTouched(formikTouched, false);
      }

      throw error;
    }
  };

  // Auto-fill first payment date = maturity date for Balloon/Partial Balloon modes
  useEffect(() => {
    if (formik.dirty) {
      const autoValues = calculateAutoFilledValues(formik.values);
      Object.entries(autoValues).forEach(([key, value]) => {
        formik.setFieldValue(key, value);
      });
    }
  }, [formik.values.payment_term, formik.values.maturity_date, formik.values.loan_amount, formik.values.is_withdrawal]);

  // Track dirty state
  useEffect(() => {
    if (formik.dirty) {
      markTabDirty();
    }
  }, [formik.dirty, markTabDirty]);

  // Reset form values when switching tabs
  useEffect(() => {
    let baseValues: FormValues;
    if (currentSegment) {
      // Edit existing segment
      baseValues = {
        ...currentSegment,
        id: currentSegment?.id || 0,
        deal_title: currentSegment?.deal_title || "",
        fees: isFacilityTab ? fees || [] : [],
        interest_index_value: null,
        principal_index_value: null,
        pik_index_value: null,
        custom_payments_file: null
      };
    } else if (currentPrimaryTab && currentPrimaryTab.segments.length > 0) {
      const withdrawalSegment = currentPrimaryTab.segments[currentPrimaryTab.segments.length - 1];
      // Add new segment to existing deal
      baseValues = {
        ...withdrawalSegment,
        id: 0,
        deal_id: currentPrimaryTab.dealId,
        deal_title: currentPrimaryTab.dealTitle,
        fees: isFacilityTab ? fees || [] : [],
        interest_index_value: null,
        principal_index_value: null,
        pik_index_value: null,
        custom_payments_file: null
      };
    } else if (facilitySegments.length > 0) {
      // Add Withdrawal
      const facilitySegment = facilitySegments[facilitySegments.length - 1];
      baseValues = {
        ...facilitySegment,
        id: 0,
        deal_title: "",
        interest_index_value: null,
        principal_index_value: null,
        pik_index_value: null,
        custom_payments_file: null,
        is_facility: false,
        is_withdrawal: true,
        fees: []
      } as FormValues;
    } else {
      baseValues = defaultFacilityValues;
    }

    // Include auto-filled values in initial values to prevent dirty state
    const autoFilledValues = calculateAutoFilledValues(baseValues);

    formik.resetForm({
      values: {
        ...baseValues,
        ...autoFilledValues
      } as FormValues
    });
  }, [primaryTabIndex, secondaryTabIndex, currentPrimaryTab?.dealId]);

  return {
    formik,
    handleSubmit,
    getSegmentFromFormValues
  };
}
